/** A text homeowners read that the company may write itself (docs/features/homeowner-language.md, "The company's own
 * texts"). Off (the default): our text, translated into each homeowner's language, shown read-only here in the
 * company's main language. On ("Write my own text"): the company's own words, shown exactly as written in every
 * language; turning it on starts from our text, turning it off asks first and goes back to ours (null). */
import { useId, useState, type ReactNode } from "react";
import { useT } from "../i18n/I18nProvider";
import { ownTextError, startOwnText } from "../lib/ownText";
import { Button, Modal, cx } from "./ui";

/** The field's label with the "Write my own text" switch, and the confirmation before going back to our text. */
export function OwnTextHead({ id, label, own, disabled, onOn, onOff }: {
  id: string;
  label: ReactNode;
  own: boolean;
  disabled?: boolean;
  onOn: () => void;
  onOff: () => void;
}) {
  const t = useT();
  const [asking, setAsking] = useState(false);
  return (
    <div className="own-text-head">
      <label id={`${id}-label`} htmlFor={`${id}-text`}>{label}</label>
      <label className="switch own-text-switch">
        <input
          type="checkbox"
          checked={own}
          disabled={disabled}
          aria-labelledby={`${id}-switch ${id}-label`}
          onChange={(e) => (e.target.checked ? onOn() : setAsking(true))}
        />
        <span className="track" />
        <span id={`${id}-switch`}>{t("common.ownText.switch")}</span>
      </label>
      {asking && (
        <Modal
          title={t("common.ownText.offTitle")}
          onClose={() => setAsking(false)}
          width={460}
          footer={
            <>
              <Button variant="ghost" onClick={() => setAsking(false)}>{t("common.ownText.keep")}</Button>
              <Button variant="primary" onClick={() => (setAsking(false), onOff())}>{t("common.ownText.offConfirm")}</Button>
            </>
          }
        >
          <p style={{ margin: 0 }}>{t("common.ownText.offBody", { field: typeof label === "string" ? label : "" })}</p>
        </Modal>
      )}
    </div>
  );
}

/** One text: `value` null is ours (`ours`, our text in the company's main language, shown read-only), a string the
 * company's own. `show` turns a stored text into what the reader sees (the `{company}` token named). */
export function OwnTextField({ label, value, ours, onChange, multiline, maxLength, allowEmpty, oursHint, ownHint, disabled, show, className }: {
  label: string;
  value: string | null | undefined;
  ours: string | undefined;
  onChange: (value: string | null) => void;
  multiline?: boolean;
  maxLength: number;
  /** An empty own text means "none" (the subheadline) instead of an error. */
  allowEmpty?: boolean;
  oursHint?: ReactNode;
  ownHint?: ReactNode;
  disabled?: boolean;
  show?: (text: string) => string;
  className?: string;
}) {
  const t = useT();
  const id = useId();
  const own = value != null;
  const error = own ? ownTextError(value, allowEmpty) : null;
  const common = { id: `${id}-text`, dir: "auto", "aria-describedby": `${id}-hint` } as const;
  const shown = ours == null ? "" : show ? show(ours) : ours;
  return (
    <div className={cx("field own-text", own && "on", className)} data-own-text={own ? "own" : "ours"}>
      <OwnTextHead id={id} label={label} own={own} disabled={disabled} onOn={() => onChange(startOwnText(ours))} onOff={() => onChange(null)} />
      {own ? (
        multiline ? (
          <textarea {...common} className="textarea" maxLength={maxLength} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        ) : (
          <input {...common} className="input" maxLength={maxLength} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        )
      ) : multiline ? (
        <textarea {...common} className="textarea own-text-ours" readOnly value={shown} />
      ) : (
        <input {...common} className="input own-text-ours" readOnly value={shown} />
      )}
      {error ? (
        <span className="field-error" id={`${id}-hint`}>{t("common.ownText.empty")}</span>
      ) : (
        <span className="hint" id={`${id}-hint`}>{own ? (ownHint ?? t("common.ownText.own")) : (oursHint ?? t("common.ownText.ours"))}</span>
      )}
    </div>
  );
}
